import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Search, Filter, Download, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMemo, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { useDebounce } from 'use-debounce';
import {
  useInfiniteDepartmentsQuery,
  useInfiniteTouchpointsQuery,
  useInfiniteSubTouchpointsQuery,
  useInfiniteTitlesQuery,
} from '@/hooks/queries/useTemplates';

interface Standard {
  id: string;
  text: string;
  department: string;
  touchpoint: string;
  subtouchpoint: string;
  title: string;
}

interface FiltersProps {
  standards: Standard[];
  propertyTypeId: string | number;
  searchQuery: string;
  departmentFilter: number | undefined;
  touchpointFilter: number | undefined;
  subTouchpointFilter: number | undefined;
  titleFilter: number | undefined;
  onSearchChange: (query: string) => void;
  onDepartmentChange: (departmentId: number | undefined) => void;
  onTouchpointChange: (touchpointId: number | undefined) => void;
  onSubTouchpointChange: (subTouchpointId: number | undefined) => void;
  onTitleChange: (titleId: number | undefined) => void;
  onClearFilters: () => void;
  onExportData?: () => void;
  onSearch?: (query: string) => void;
}

export function StandardsFilters({
  propertyTypeId,
  searchQuery,
  departmentFilter,
  touchpointFilter,
  subTouchpointFilter,
  titleFilter,
  onSearchChange,
  onDepartmentChange,
  onTouchpointChange,
  onSubTouchpointChange,
  onTitleChange,
  onClearFilters,
  onExportData,
  onSearch,
}: FiltersProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  // Internal state for immediate input updates
  const [internalSearchValue, setInternalSearchValue] = useState(searchQuery);

  // Debounce the internal value
  const [debouncedSearch] = useDebounce(internalSearchValue, 500);

  // Sync internal value with prop changes (e.g., when filters are cleared)
  useEffect(() => {
    setInternalSearchValue(searchQuery);
  }, [searchQuery]);

  // Call parent's onSearchChange only with debounced value
  useEffect(() => {
    if (debouncedSearch !== searchQuery) {
      onSearchChange(debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Call onSearch with debounced value if provided
  useEffect(() => {
    if (onSearch) {
      onSearch(debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Fetch filter options from API - fetch all items in single request
  const { data: departmentsData } = useInfiniteDepartmentsQuery(
    propertyTypeId,
    { page_size: 1000 }
  );
  const departments = departmentsData?.pages.flatMap(page => page.data) || [];

  const { data: touchpointsData } = useInfiniteTouchpointsQuery(
    propertyTypeId,
    { page_size: 1000 }
  );
  const touchpoints = touchpointsData?.pages.flatMap(page => page.data) || [];

  const { data: subTouchpointsData } = useInfiniteSubTouchpointsQuery(
    propertyTypeId,
    { page_size: 1000 }
  );
  const subTouchpoints =
    subTouchpointsData?.pages.flatMap(page => page.data) || [];

  const { data: titlesData } = useInfiniteTitlesQuery(propertyTypeId, { page_size: 1000 });
  const titles = titlesData?.pages.flatMap(page => page.data) || [];

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 lg:p-6">
        <div
          className={cn(
            'flex flex-col xs:flex-row xs:items-center xs:justify-between gap-3 mb-4',
            isRTL ? 'xs:flex-row-reverse' : 'xs:flex-row'
          )}
        >
          <div
            className={cn(
              'flex items-center gap-2',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
            <Label className="text-base sm:text-lg font-semibold">
              {t('templates.admin.detail.filters.filtersAndSearch')}
            </Label>
          </div>
          <div
            className={cn(
              'flex flex-col xs:flex-row items-stretch xs:items-center gap-2 w-full xs:w-auto',
              isRTL ? 'xs:flex-row-reverse' : 'xs:flex-row'
            )}
          >
            <Button
              variant="outline"
              size="sm"
              className="w-full xs:w-auto text-xs sm:text-sm"
              onClick={onClearFilters}
            >
              <X
                className={cn(
                  'w-3 h-3 sm:w-4 sm:h-4',
                  isRTL ? 'ml-1 sm:ml-2' : 'mr-1 sm:mr-2'
                )}
              />
              {t('templates.admin.detail.filters.clearAll')}
            </Button>
            {onExportData && (
              <Button
                variant="default"
                onClick={onExportData}
                size="sm"
                className="w-full xs:w-auto text-xs sm:text-sm"
              >
                <Download
                  className={cn(
                    'w-3 h-3 sm:w-4 sm:h-4',
                    isRTL ? 'ml-1 sm:ml-2' : 'mr-1 sm:mr-2'
                  )}
                />
                {t('templates.admin.detail.filters.exportData')}
              </Button>
            )}
          </div>
        </div>

        {/* Search Input */}
        <div className="mb-3 sm:mb-4">
          <Label
            htmlFor="search"
            className={cn('text-sm sm:text-base', isRTL && 'text-right')}
          >
            {t('templates.admin.detail.filters.globalSearch')}
          </Label>
          <div className="relative mt-1">
            <Search
              className={cn(
                'absolute top-2.5 sm:top-3 h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground',
                isRTL ? 'right-3' : 'left-3'
              )}
            />
            <Input
              id="search"
              placeholder={t('templates.admin.detail.filters.searchPlaceholder')}
              value={internalSearchValue}
              onChange={e => setInternalSearchValue(e.target.value)}
              className={cn(
                'text-sm sm:text-base h-8 sm:h-10',
                isRTL ? 'pr-8 sm:pr-10 text-right' : 'pl-8 sm:pl-10'
              )}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
        </div>

        {/* Filter Dropdowns */}
        <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 lg:gap-4">
          <div>
            <Label
              htmlFor="department"
              className={cn(
                'text-xs sm:text-sm mb-1 block',
                isRTL && 'text-right'
              )}
            >
              {t('templates.admin.detail.filters.department')}
            </Label>
            <Select
              value={departmentFilter ? String(departmentFilter) : '__all__'}
              onValueChange={value =>
                onDepartmentChange(
                  value === '__all__' ? undefined : Number(value)
                )
              }
            >
              <SelectTrigger
                className={cn(
                  'h-8 sm:h-10 text-xs sm:text-sm',
                  isRTL && 'text-right'
                )}
              >
                <SelectValue
                  placeholder={t('templates.admin.detail.filters.allDepartmentsPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">
                  {t('templates.admin.detail.filters.allDepartments')}
                </SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept.id} value={String(dept.id)}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label
              htmlFor="touchpoint"
              className={cn(
                'text-xs sm:text-sm mb-1 block',
                isRTL && 'text-right'
              )}
            >
              {t('templates.admin.detail.filters.touchpoint')}
            </Label>
            <Select
              value={touchpointFilter ? String(touchpointFilter) : '__all__'}
              onValueChange={value =>
                onTouchpointChange(
                  value === '__all__' ? undefined : Number(value)
                )
              }
            >
              <SelectTrigger
                className={cn(
                  'h-8 sm:h-10 text-xs sm:text-sm',
                  isRTL && 'text-right'
                )}
              >
                <SelectValue
                  placeholder={t('templates.admin.detail.filters.allTouchpointsPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">
                  {t('templates.admin.detail.filters.allTouchpoints')}
                </SelectItem>
                {touchpoints.map(tp => (
                  <SelectItem key={tp.id} value={String(tp.id)}>
                    {tp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label
              htmlFor="subtouchpoint"
              className={cn(
                'text-xs sm:text-sm mb-1 block',
                isRTL && 'text-right'
              )}
            >
              {t('templates.admin.detail.filters.subtouchpoint')}
            </Label>
            <Select
              value={
                subTouchpointFilter ? String(subTouchpointFilter) : '__all__'
              }
              onValueChange={value =>
                onSubTouchpointChange(
                  value === '__all__' ? undefined : Number(value)
                )
              }
            >
              <SelectTrigger
                className={cn(
                  'h-8 sm:h-10 text-xs sm:text-sm',
                  isRTL && 'text-right'
                )}
              >
                <SelectValue
                  placeholder={t('templates.admin.detail.filters.allSubTouchpointsPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">
                  {t('templates.admin.detail.filters.allSubTouchpoints')}
                </SelectItem>
                {subTouchpoints.map(stp => (
                  <SelectItem key={stp.id} value={String(stp.id)}>
                    {stp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label
              htmlFor="title"
              className={cn(
                'text-xs sm:text-sm mb-1 block',
                isRTL && 'text-right'
              )}
            >
              {t('templates.admin.detail.filters.title')}
            </Label>
            <Select
              value={titleFilter ? String(titleFilter) : '__all__'}
              onValueChange={value =>
                onTitleChange(value === '__all__' ? undefined : Number(value))
              }
            >
              <SelectTrigger
                className={cn(
                  'h-8 sm:h-10 text-xs sm:text-sm',
                  isRTL && 'text-right'
                )}
              >
                <SelectValue
                  placeholder={t('templates.admin.detail.filters.allTitlesPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">
                  {t('templates.admin.detail.filters.allTitles')}
                </SelectItem>
                {titles.map(title => (
                  <SelectItem key={title.id} value={String(title.id)}>
                    {title.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="xs:col-span-2 sm:col-span-3 lg:col-span-1">
            {/* Empty placeholder for consistency - removed standard text filter as it's redundant with search */}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
